import { useState } from 'react';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from './ui/dialog';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

/**
 * Reusable button + dialog for logging non-task client time
 * (downtime / meeting / other). Available to all roles.
 */
export const LogClientTimeButton = ({ clients = [], onLogged, size = 'sm', variant = 'outline' }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    client_id: '', category: 'meeting',
    log_date: new Date().toISOString().slice(0, 10),
    hours: '', description: '',
  });

  const reset = () => setForm({
    client_id: '', category: 'meeting',
    log_date: new Date().toISOString().slice(0, 10),
    hours: '', description: '',
  });

  const submit = async () => {
    setSaving(true);
    try {
      await api.post('/client-time-logs', {
        client_id: form.client_id,
        category: form.category,
        log_date: form.log_date,
        hours: parseFloat(form.hours),
        description: form.description || null,
      });
      toast.success('Client time logged');
      setOpen(false);
      reset();
      if (onLogged) onLogged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to log time');
    } finally {
      setSaving(false);
    }
  };

  const disabled = saving || !form.client_id || !form.log_date || !form.hours || parseFloat(form.hours) <= 0;

  return (
    <>
      <Button
        size={size}
        variant={variant}
        onClick={() => { reset(); setOpen(true); }}
        className="text-indigo-600 hover:text-indigo-700 border-indigo-300 hover:bg-indigo-50"
        data-testid="log-client-time-btn"
      >
        <Clock className="h-4 w-4 mr-1.5" /> Log Client Time
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="ctl-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-indigo-600" />
              Log Client Time
            </DialogTitle>
            <DialogDescription>
              Log downtime, meetings, or other non-task time against a client. Hours are non-billable but appear in the timesheet.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Client <span className="text-red-500">*</span></Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="ctl-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.company}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Category <span className="text-red-500">*</span></Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="ctl-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="meeting">Meeting</SelectItem>
                  <SelectItem value="downtime">Downtime</SelectItem>
                  <SelectItem value="gst_visit">GST Visit</SelectItem>
                  <SelectItem value="call_for_data">Call for Data</SelectItem>
                  <SelectItem value="transition">Transition</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Date <span className="text-red-500">*</span></Label>
                <Input type="date" value={form.log_date}
                  onChange={(e) => setForm({ ...form, log_date: e.target.value })}
                  data-testid="ctl-date" />
              </div>
              <div>
                <Label className="text-sm">Hours <span className="text-red-500">*</span></Label>
                <Input type="number" step="0.25" min="0.25" max="24"
                  value={form.hours}
                  onChange={(e) => setForm({ ...form, hours: e.target.value })}
                  placeholder="e.g. 1.5"
                  data-testid="ctl-hours" />
              </div>
            </div>
            <div>
              <Label className="text-sm">Notes (optional)</Label>
              <Textarea rows={2} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What was discussed / why this time was logged"
                data-testid="ctl-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              data-testid="ctl-submit"
              className="bg-indigo-600 hover:bg-indigo-700"
              disabled={disabled}
              onClick={submit}
            >
              {saving ? 'Saving…' : 'Log Time'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
